import { useEffect, useRef, useState } from 'react'

import { SlideToggle } from '@syconium/little-miss-figgy'

import { useLocalization } from '../../../../app/_providers/LocalizationProvider.client'
import { useTranslation } from '../../../../app/_providers/TranslationProvider.client'
import {
  SizeChartTableCells,
  SizeChartTable as SizeChartTableData,
  SizeChartTip,
} from '../../../../components/pages/products/ProductPage/DetailsSection/SizesSection/SizeChart'
import { useShadowState } from '../../../../lib/hooks/useShadowState'

import {
  Body,
  MeasureSection,
  MeasureTipItem,
  MeasureTipKind,
  MeasureTipsList,
  MeasureTipsListItem,
  MeasureWrapper,
  ResponsiveImage,
  SizeTipKind,
  SizeTipText,
  SizeTipTextWrap,
  SizeTipWrap,
  Slider,
  SliderWrap,
  Subtitle,
  Table,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TitleBar,
  ToggleWrap,
} from './styles'
type SizeChartCellsByUnit = {
  centimeters: SizeChartTableCells
  inches: SizeChartTableCells
}
export type SizeChartTableProps = SizeChartTableData & {
  metric?: boolean | null
  onToggleUnit?: () => void | null
}
function isTableCells(cells: unknown): cells is SizeChartTableCells {
  return Array.isArray(cells)
}
function isCellsByUnits(cells: unknown): cells is SizeChartCellsByUnit {
  if (typeof cells !== 'object' || cells === null) return false
  if (!('centimeters' in cells) || ('centimeters' in cells && !Array.isArray(cells.centimeters))) {
    return false
  }
  if (!('inches' in cells) || ('inches' in cells && !Array.isArray(cells.inches))) {
    return false
  }
  return true
}
export const SizeChartTable: React.FC<SizeChartTableProps> = ({
  cells,
  measureImage,
  measureTips,
  sizeTips,
  subtitle,
  title,
}) => {
  const {
    sizesSection: { sizeChart: strings },
  } = useTranslation()
  const { isUSRegion } = useLocalization()
  const defaultToMetric = isUSRegion ? false : true
  const [showMetric, setShowMetric] = useState<boolean>(defaultToMetric)
  const scrollRef = useRef<HTMLDivElement>(null)

  const parsedCells = JSON.parse(cells)
  const computedCellsByUnits = isCellsByUnits(parsedCells) ? parsedCells : undefined
  const computedCells = computedCellsByUnits
    ? computedCellsByUnits[showMetric ? 'centimeters' : 'inches']
    : isTableCells(parsedCells)
    ? parsedCells
    : []

  const { shadowState, updateScrollState } = useShadowState(scrollRef)
  const labels = [
    { label: strings.toggleLabels.inches },
    { label: strings.toggleLabels.centimeters },
  ]

  const onToggle = () => {
    setShowMetric(!showMetric)
  }

  useEffect(() => {
    updateScrollState()
    // TODO: Fix this auto-generated suppression.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cells])

  return (
    <Body tabIndex={0} role='tabpanel' id='sizeChartTabPanel' aria-labelledby='sizeChartTab'>
      <TitleBar asTag='div'>
        {title}
        {computedCellsByUnits && (
          <ToggleWrap>
            <SlideToggle labels={labels} checked={showMetric} onChange={onToggle} />
          </ToggleWrap>
        )}
      </TitleBar>
      {subtitle && <Subtitle asTag='h3'>{subtitle}</Subtitle>}
      {sizeTips?.length ? (
        <SizeTipWrap>
          {sizeTips.map(tip => (
            <SizeTipTextWrap>
              <SizeTipKind>{tip.kind}: </SizeTipKind>
              <SizeTipText> {tip.tip}</SizeTipText>
            </SizeTipTextWrap>
          ))}
        </SizeTipWrap>
      ) : null}
      <SliderWrap showShadow={shadowState === 'right' || shadowState === 'both'}>
        <Slider ref={scrollRef}>
          <Table tabIndex={0}>
            <TableHead>
              <TableRow>
                {computedCells?.length
                  ? computedCells[0]?.map((cell, i) => (
                      <TableHeader
                        asTag='th'
                        key={`${cell}-${i}`}
                        showShadow={shadowState === 'left' || shadowState === 'both'}
                      >
                        {cell}
                      </TableHeader>
                    ))
                  : null}
              </TableRow>
            </TableHead>
            <tbody>
              {computedCells?.length
                ? computedCells.slice(1).map((data, i) => (
                    <TableRow key={`${data}-${i}`}>
                      {data.map((cell, index) => (
                        <TableCell
                          key={`${cell}-${index}`}
                          showShadow={shadowState === 'left' || shadowState === 'both'}
                        >
                          {cell}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                : null}
            </tbody>
          </Table>
        </Slider>
      </SliderWrap>
      <HowToMeasure
        measureImage={measureImage}
        measureTips={measureTips ?? []}
        howToMeasure={strings.howToMeasure}
        imageAlt={strings.imageAlt}
      />
    </Body>
  )
}

const HowToMeasure: React.FC<{
  howToMeasure: string
  imageAlt: string
  measureImage: string | null | undefined
  measureTips: Array<SizeChartTip>
}> = ({ howToMeasure, imageAlt, measureImage, measureTips }) => {
  return measureImage && measureTips ? (
    <>
      <TitleBar>
        <h2>{howToMeasure}</h2>
      </TitleBar>
      <MeasureWrapper>
        <MeasureSection>
          <ResponsiveImage
            alt={imageAlt}
            src={measureImage}
            aspectRatios={{
              sm: 1 / 1,
              md: 1 / 1,
            }}
            widths={{ unit: 'vw', sm: 100, md: 30 }}
            loading='default'
          />
        </MeasureSection>
        <MeasureSection>
          <MeasureTipsList>
            {measureTips.map(item => (
              <MeasureTipsListItem key={`${item.kind}-${item.tip}`}>
                <MeasureTipKind asTag='p'>{item.kind}</MeasureTipKind>
                <MeasureTipItem asTag='p'>{item.tip}</MeasureTipItem>
              </MeasureTipsListItem>
            ))}
          </MeasureTipsList>
        </MeasureSection>
      </MeasureWrapper>
    </>
  ) : null
}
